import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Helmet } from "react-helmet";
import { supabase } from "@/integrations/supabase/client";
import { BagDetails } from "@/components/BagDetails";
import { Header } from "@/components/Header";
import { AboutCreator } from "@/components/AboutCreator";
import { useToast } from "@/components/ui/use-toast";
import { BagImageUpload } from "@/components/bag/BagImageUpload";

const formatBagData = (bagData: any) => {
  if (!bagData) {
    console.error('formatBagData received null/undefined data');
    return null;
  }
  
  console.log('Starting to format bag data:', bagData);
  
  try {
    const formatted = {
      id: bagData.id,
      styleNumber: bagData.style_number?.toString().padStart(4, '0') || "",
      alternateStyleNumber: bagData.alternate_style_number || undefined,
      name: bagData.Name || "",
      description: bagData.description || "",
      measurements: {
        height: bagData.Height || "Not specified",
        width: bagData.Width || "Not specified",
        depth: bagData.Depth || "Not specified",
        strap: bagData.Strap || undefined,
        alternate: bagData.Alternate_Measurements || undefined
      },
      years: bagData.Years_in_Production || "Unknown",
      alternativeName: bagData.alternate_name || undefined,
      colors: bagData.Colors || undefined,
      mainImage: bagData.main_image || undefined
    };
    
    console.log('Successfully formatted bag data:', formatted);
    return formatted;
  } catch (error) {
    console.error('Error formatting bag data:', error);
    return null;
  }
};

const BagPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();

  console.log('Current UUID param:', id);

  const { data: bag, isLoading, error } = useQuery({
    queryKey: ["bag", id],
    queryFn: async () => {
      if (!id) {
        console.log('No ID provided');
        return null;
      }
      
      console.log('Starting Supabase query for UUID:', id);
      
      const { data, error } = await supabase
        .from("coach_bag_real_data")
        .select(`
          id,
          style_number,
          alternate_style_number,
          Name,
          description,
          Height,
          Width,
          Depth,
          Strap,
          Alternate_Measurements,
          Years_in_Production,
          alternate_name,
          Colors,
          main_image
        `)
        .eq("id", id)
        .maybeSingle();

      if (error) {
        console.error('Supabase error:', error);
        toast({
          title: "Error",
          description: "Failed to load bag details. Please try again.",
          variant: "destructive",
        });
        throw error;
      }

      console.log('Supabase response:', { data, error });

      if (!data) {
        console.log('No bag found with UUID:', id);
        toast({
          title: "Not Found",
          description: "The requested bag could not be found.",
          variant: "destructive",
        });
        return null;
      }

      return formatBagData(data);
    },
    retry: false
  });

  console.log('Query result:', { bag, isLoading, error });

  if (error) {
    console.error('Query error:', error);
  }

  return (
    <>
      <Helmet>
        <title>{bag?.name ? `${bag.name} - Style ${bag?.styleNumber}` : 'Vintage Coach Bag Details'}</title>
        <meta 
          name="description" 
          content={bag ? 
            `View details for vintage Coach ${bag.name || `style ${bag.styleNumber}`}. Find measurements, production years, and authentic features of this classic Coach design.` : 
            'Detailed information about this vintage Coach bag style, including measurements and production details.'
          } 
        />
      </Helmet>
      <main className="min-h-screen w-full bg-background flex flex-col">
        <div className="flex-grow max-w-4xl mx-auto px-4 pt-20 pb-12">
          <Header />
          
          {error ? (
            <div className="text-center py-8">
              <p className="text-red-600">Failed to load bag details. Please try again.</p>
            </div>
          ) : (
            <div className="space-y-8 mt-8">
              <BagDetails bag={bag} isLoading={isLoading} />
            </div>
          )}
        </div>
        <AboutCreator />
      </main>
    </>
  );
};

export default BagPage;
